import { GTMPurchasedAccommodationItem } from "@/helpers/gtm/GTMAccommodationItem.ts";
import { GTMEvent } from "@/helpers/gtm/GTMEvent.ts";
import { PurchaseEcommerce } from "@/helpers/gtm/PurchaseEcommerce.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import TagManager from "react-gtm-module";
import { useShallow } from "zustand/react/shallow";

// Extra names matched by display string (no stable id on the API); keep in sync with backend.
const EXTRA_NAME = {
  breakfast: "Dodatan doručak",
  car: "Dodatan auto",
  pet: "Kućni ljubimac",
  babyGear: "Oprema za bebe",
  sojournTax: "Boravišna pristojba (odrasli)",
  checkInFee: "Jednokratna prijava smještaja",
} as const;

// Standalone so a deferred purchase can be fired on the success return, without cart state.
export const pushPurchaseEvent = (ecommerce: PurchaseEcommerce) => {
  TagManager.dataLayer({ dataLayer: { ecommerce: null } });
  TagManager.dataLayer({
    dataLayer: {
      event: GTMEvent.Purchase,
      ecommerce,
    },
  });
};

export const useGTMPurchase = () => {
  const { cartAccommodationItems, calculatedCartData } = useEngineStore(
    useShallow((state) => ({
      cartAccommodationItems: state.cartAccommodationItems,
      calculatedCartData: state.calculatedCartData,
    })),
  );

  const buildPurchaseEcommerce = (reservationID: string): PurchaseEcommerce => {
    const purchasedItems = cartAccommodationItems?.map((cartItem, index): GTMPurchasedAccommodationItem => {
      const RP = cartItem.ratePlan;
      const RG = cartItem.ratePlan.rateGroup;

      const breakfastCount =
        cartItem.optionalExtras?.filter((e) => e.name === EXTRA_NAME.breakfast)?.length ?? 0;
      const carCount = cartItem.optionalExtras?.filter((e) => e.name === EXTRA_NAME.car)?.length ?? 0;
      const petCount = cartItem.optionalExtras?.filter((e) => e.name === EXTRA_NAME.pet)?.length ?? 0;
      const babyGearCount =
        cartItem.optionalExtras?.filter((e) => e.name === EXTRA_NAME.babyGear)?.length ?? 0;
      const adults = cartItem.adults?.length ?? 0;
      const children = cartItem.children?.length ?? 0;

      return {
        item_id: RP.id,
        item_name: RP.name,
        currency: "EUR",
        price: RP.defaultPriceBreakdown.withDiscount,
        item_category: RG.name,
        index,
        quantity: 1,

        breakfast: breakfastCount,
        baby_gear: babyGearCount,
        car: carCount,
        pets: petCount,
        adults,
        children,
      };
    });

    const sojournTaxMandatoryExtras = cartAccommodationItems
      ?.flatMap((item) => item.mandatoryExtras)
      ?.filter((extra) => extra?.name === EXTRA_NAME.sojournTax);
    const totalSojournTax = (sojournTaxMandatoryExtras ?? []).reduce((acc, item) => acc + item.price, 0);

    const checkInFeeMandatoryExtras = cartAccommodationItems
      ?.flatMap((item) => item.mandatoryExtras)
      ?.filter((extra) => extra?.name === EXTRA_NAME.checkInFee);
    const totalCheckInFee = (checkInFeeMandatoryExtras ?? []).reduce((acc, item) => acc + item.price, 0);

    return {
      currency: "EUR",
      value: calculatedCartData?.priceBreakdown?.withDiscount,

      transaction_id: reservationID,
      sojourn_tax: totalSojournTax,
      check_in_fee: totalCheckInFee,

      items: purchasedItems,
    };
  };

  const firePurchase = (reservationID: string) => {
    pushPurchaseEvent(buildPurchaseEcommerce(reservationID));
  };

  return { firePurchase, buildPurchaseEcommerce };
};
